/**
 * Shared client print chrome for Tabulator printAsHtml / detail prints (Phase 3).
 * Company logo via resolvePrintCompanyLogoSrc; print date via Phase 2 formatters.
 * Does not re-implement server CompanyPrintLayoutHtmlRenderer bands — those stay
 * on /export/print. This helper stops per-page handcrafted &lt;h1&gt; + product-logo rows.
 */

import { DateTime } from 'luxon';
import { PRINT_HEADER_LOGO_REM } from '../constants/brandingDisplay';
import { resolvePrintCompanyLogoSrc, getCompanyIdentity } from './companyBranding';
import { formatDate, DEFAULT_DATE_FORMAT } from './formatters';
import { getDateFormat, getTimezone } from './moduleSettings';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Today stamp for print chrome using global date_format + timezone.
 * @param {unknown} moduleSettings
 * @returns {string}
 */
export function formatPrintToday(moduleSettings) {
  const dateFormat = getDateFormat(moduleSettings) || DEFAULT_DATE_FORMAT;
  const timezone = getTimezone(moduleSettings) || 'Europe/Bratislava';
  const iso = DateTime.now().setZone(timezone).toISO();
  return formatDate(iso, { dateFormat, timezone, empty: '' });
}

/**
 * Compact footer lines from company_settings_print (parity with 8D / settings preview).
 * @param {Record<string, unknown> | null | undefined} csp
 */
export function buildFooterCompanyLinesFromCsp(csp) {
  if (!csp) {
    return { companyName: '', location: '', taxLine: '', contactLine: '', bankLine: '' };
  }
  const identity = getCompanyIdentity(csp);
  const streetZipCity = [(csp.street || '').trim(), [csp.zip, csp.city].filter(Boolean).join(' ').trim()]
    .filter(Boolean)
    .join(', ');
  const location = [streetZipCity, (csp.country || '').trim()].filter(Boolean).join(', ');
  const taxLine = identity.identification;
  const contactLine = [
    csp.phone && `Tel.: ${csp.phone}`,
    (csp.secondary_phone || '').trim() || null,
    csp.email && `Mail: ${csp.email}`,
    (csp.secondary_email || '').trim() || null,
    csp.website && `www: ${csp.website}`,
  ]
    .filter(Boolean)
    .join(' · ');
  const bankLine = [
    (csp.bank_name || '').trim() || null,
    csp.iban && `IBAN: ${csp.iban}`,
    csp.swift && `SWIFT: ${csp.swift}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    companyName: identity.companyName,
    location,
    taxLine,
    contactLine,
    bankLine,
  };
}

export function printFooterPlainText(csp) {
  if (!csp) return '';
  const p = String(csp.print_footer_text || '').trim();
  const f = String(csp.footer_text || '').trim();
  return p || f || '';
}

/**
 * Canonical Tabulator printHeader HTML: logo + title + optional print date + optional body.
 *
 * @param {{
 *   branding?: Record<string, unknown> | null,
 *   productFallbackSrc: string,
 *   companySettingsPrint?: Record<string, unknown> | null,
 *   moduleSettings?: unknown,
 *   title: string,
 *   bodyHtml?: string,
 *   printDateLabel?: string,
 * }} opts
 * @returns {string}
 */
export function buildCanonicalPrintHeaderHtml(opts) {
  const {
    branding = null,
    productFallbackSrc,
    companySettingsPrint = null,
    moduleSettings = null,
    title,
    bodyHtml = '',
    printDateLabel = 'Print date',
  } = opts || {};

  const logoSrc = escapeHtml(resolvePrintCompanyLogoSrc(branding, productFallbackSrc));
  const safeTitle = escapeHtml(title || '');
  const showDate = companySettingsPrint == null
    ? true
    : companySettingsPrint.show_print_date !== false && companySettingsPrint.show_print_date !== 0;
  const today = formatPrintToday(moduleSettings);
  const dateLine = showDate && today
    ? `<div style="font-size:11px;color:#4b5563;text-align:right;margin-top:4px;">${escapeHtml(printDateLabel)}: ${escapeHtml(today)}</div>`
    : '';
  const centerText = String(companySettingsPrint?.print_header_center_text || '').trim();
  const centerBlock = centerText
    ? `<div style="text-align:center;font-size:12px;white-space:pre-wrap;color:#18181b;">${escapeHtml(centerText)}</div>`
    : '';

  const logoRem = PRINT_HEADER_LOGO_REM;
  return (
    `<div class="legro-print-chrome-header" style="width:100%;box-sizing:border-box;">` +
    `<div style="display:flex;flex-wrap:wrap;justify-content:space-between;align-items:flex-start;gap:8px;border-bottom:1px solid #e5e7eb;padding-bottom:8px;margin-bottom:8px;">` +
    `<div style="flex:0 0 auto;max-width:180px;"><img src="${logoSrc}" alt="" style="height:${logoRem}rem;width:auto;max-width:100%;object-fit:contain;" /></div>` +
    `<div style="flex:1 1 160px;min-width:120px;">${centerBlock}</div>` +
    `<div style="flex:0 1 220px;text-align:right;">` +
    `<h1 style="font-size:1.5rem;line-height:1.75rem;margin:0;font-weight:600;color:#18181b;text-align:right;">${safeTitle}</h1>` +
    dateLine +
    `</div></div>` +
    (bodyHtml || '') +
    `</div>`
  );
}

/**
 * Canonical Tabulator printFooter HTML when unified footer is enabled.
 *
 * @param {{
 *   companySettingsPrint?: Record<string, unknown> | null,
 * }} opts
 * @returns {string}
 */
export function buildCanonicalPrintFooterHtml(opts = {}) {
  const csp = opts.companySettingsPrint || null;
  if (!csp) return '';
  const useFooter = !!csp.use_unified_footer;
  if (!useFooter) return '';

  const lines = buildFooterCompanyLinesFromCsp(csp);
  const plain = printFooterPlainText(csp);
  const parts = [
    lines.companyName,
    lines.location,
    lines.taxLine,
    lines.contactLine,
    lines.bankLine,
    plain,
  ].filter(Boolean);
  if (parts.length === 0) return '';

  return (
    `<div class="legro-print-chrome-footer" style="width:100%;box-sizing:border-box;border-top:1px solid #e5e7eb;margin-top:12px;padding-top:8px;font-size:10px;line-height:1.35;color:#4b5563;">` +
    parts.map((p) => `<div style="white-space:pre-wrap;">${escapeHtml(p)}</div>`).join('') +
    `</div>`
  );
}

/**
 * Convenience: header + footer for Tabulator printAsHtml surfaces.
 */
export function buildCanonicalTabulatorPrintChrome(opts) {
  const header = buildCanonicalPrintHeaderHtml(opts);
  const footer = buildCanonicalPrintFooterHtml(opts);
  return { header, footer };
}
